// Alpine.js Payment Method Component
// This replaces the complex Stimulus controller with a much cleaner, reactive approach
import Alpine from 'alpinejs';
import { loadStripe } from '@stripe/stripe-js';
import {
  createAddress,
  getAddresses,
  getBrregCompanyInformation,
  getCardsList,
} from "@/scripts/application/api";
import {
  trackCheckoutEvent,
  installCheckoutErrorCapture,
} from "@/scripts/application/checkoutEventTracker";

// Constants
const DEFAULT_COUNTRY = 'Norway';
const COMPANY_SEARCH_MIN_LENGTH = 3;
const COMPANY_SEARCH_DEBOUNCE_MS = 300;

Alpine.data('checkoutForm', () => ({
  // Stripe instance variables (per component instance)
  stripe: null,
  elements: null,
  cardElement: null,
  cardChangeHandler: null,
  companySearchTimeout: null,

  // Reactive data state
  selectedGatewayId: null,
  selectedMethod: 'stripe',
  selectedCardId: null,
  selectedInvoiceType: 'individual',
  _selectedIndividualAddressId: null,
  _selectedCompanyAddressId: null,
  termsAccepted: false,
  filteredCompanies: [],
  showCompanyDropdown: false,
  highlightedCompanyIndex: -1,
  addressList: [],
  cards: [],
  isLoading: true,
  _companyQuery: '',

  // Create fresh address objects per instance
  get _newIndividualAddress() {
    if (!this.__newIndividualAddress) {
      this.__newIndividualAddress = {
        address1: '',
        address2: '',
        billing_email: '',
        city: '',
        country: DEFAULT_COUNTRY,
        postal_code: '',
        state: '',
      };
    }
    return this.__newIndividualAddress;
  },

  get _newCompanyAddress() {
    if (!this.__newCompanyAddress) {
      this.__newCompanyAddress = {
        address1: '',
        address2: '',
        billing_email: '',
        billing_ref: '',
        city: '',
        company: '',
        contact_person: '',
        country: DEFAULT_COUNTRY,
        organization_id: '',
        postal_code: '',
        state: '',
      };
    }
    return this.__newCompanyAddress;
  },

  // Initialize component
  async init(selectedGatewayId, selectedAddressId, selectedMethod) {
    installCheckoutErrorCapture();

    // Set initial gateway ID
    if (selectedGatewayId) {
      this.selectedGatewayId = `${selectedGatewayId}`;

      // If method not provided, infer from the selected gateway's DOM element
      if (!selectedMethod) {
        const gatewayInput = document.querySelector(`#payment_gateway_${selectedGatewayId}`);
        if (gatewayInput?.dataset?.gatewayKind) {
          selectedMethod = gatewayInput.dataset.gatewayKind;
        }
      }
    }

    // Set payment method (with fallback to stripe)
    this.selectedMethod = selectedMethod || 'stripe';

    // Set initial address
    if (!!selectedAddressId) this.selectedAddressId = selectedAddressId;

    // Initialize Stripe for this component instance
    await this.initializeStripe();

    // Set up watchers
    this.$watch('selectedCardId', (value) => {
      if (value === 'new_credit_card') {
        this.$nextTick(async () => {
          await this.mountCard();
        });
      }
    });

    this.$watch('selectedInvoiceType', () => {
      this.$nextTick(() => {
        if (this.selectedAddressId === null) {
          if (this.availableAddresses.length > 0) {
            this.selectedAddressId = this.availableAddresses[0]?.id
          } else {
            this.selectedAddressId = 'new_address';
          }
        }
      });
    });

    // Watch for payment method changes to remount card element when switching back to Stripe
    this.$watch('selectedMethod', (value) => {
      if (value === 'stripe' && this.showCardElement) {
        // Wait for x-if to recreate the DOM
        this.$nextTick(async () => {
          await this.mountCard();
        });
      }
    });

    // Load initial data
    try {
      const [addresses, cards] = await Promise.all([
        getAddresses(),
        getCardsList()
      ]);

      // Validate data
      this.addressList = Array.isArray(addresses) ? addresses : [];
      this.cards = Array.isArray(cards) ? cards : [];

      // Set initial selections without triggering watchers
      if (this.cards.length > 0) {
        this.selectedCardId = this.cards[0].id;
      } else {
        this.selectedCardId = 'new_credit_card';
        // Mount card element if needed
        this.$nextTick(async () => {
          await this.mountCard();
        });
      }

      if (this.selectedAddressId === null) {
        if (this.availableAddresses.length > 0) {
          this.selectedAddressId = this.availableAddresses[0]?.id
        } else {
          this.selectedAddressId = 'new_address';
        }
      }

      this.isLoading = false;
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showError('Failed to load initial data. Please refresh the page.');
      this.isLoading = false;
    }
  },

  // Initialize Stripe for this component instance
  async initializeStripe() {
    if (!window.gon?.stripe_key) {
      console.error('Stripe publishable key is not configured');
      return { stripe: null, elements: null };
    }

    if (!this.stripe) {
      this.stripe = await loadStripe(window.gon.stripe_key);
      this.elements = this.stripe?.elements();

      if (this.elements) {
        // Create card element for this instance
        this.cardElement = this.elements.create('card', {
          hidePostalCode: true,
          classes: {
            base: "block text-sm font-medium text-center py-4 px-3 border border-gray-300 bg-gray-200 bg-opacity-70 rounded-md placeholder-gray-500 focus:border-gray-500 transition outline-none w-full shadow-sm min-h-12 focus:bg-white",
          },
          style: {
            base: {
              backgroundColor: "transparent",
              fontSize: "16px",
            },
          },
        });
      }
    }
    return { stripe: this.stripe, elements: this.elements };
  },

  get showCardElement() {
    return this.cards.length === 0 || this.selectedCardId === 'new_credit_card';
  },

  async mountCard() {
    await this.$nextTick();

    if (!this.$refs.card || !this.cardElement) return;

    try {
      this.cardElement.mount(this.$refs.card);
    } catch (error) {
      // Already mounted, ignore
      if (error.message?.includes('already been mounted')) return;
      console.error('Error mounting card element:', error);
    }
  },

  get submitDisabled() {
    return !this.termsAccepted || this.isLoading;
  },

  get companyQuery() {
    return this._companyQuery || '';
  },

  set companyQuery(value) {
    this._companyQuery = value;
    this.searchCompanies();
  },

  get newAddress() {
    return this.selectedInvoiceType === 'individual'
      ? this._newIndividualAddress
      : this._newCompanyAddress;
  },

  searchCompanies() {
    // Clear existing timeout
    if (this.companySearchTimeout) {
      clearTimeout(this.companySearchTimeout);
    }

    const value = this.companyQuery.trim();

    if (value && value.length >= COMPANY_SEARCH_MIN_LENGTH) {
      this.highlightedCompanyIndex = -1;

      // Debounce the API call
      this.companySearchTimeout = setTimeout(() => {
        getBrregCompanyInformation(value)
          .then(data => {
            this.filteredCompanies = data || [];
            this.showCompanyDropdown = (data || []).length > 0;
          })
          .catch(error => {
            console.error('Company search error:', error);
            this.filteredCompanies = [];
            this.showCompanyDropdown = false;
            this.showError('Failed to search for companies. Please try again.');
          });
      }, COMPANY_SEARCH_DEBOUNCE_MS);
    } else {
      this.filteredCompanies = [];
      this.showCompanyDropdown = false;
    }
  },

  setCompanyFromBrreg(company) {
    this._companyQuery = company.name;
    this.$refs.searchCompanyInput?.blur();

    Object.assign(this.newAddress, {
      company: company.name,
      organization_id: company.organizationId,
      address1: company.address || '',
      postal_code: company.postalCode || '',
      city: company.city || '',
      country: DEFAULT_COUNTRY
    });

    this.filteredCompanies = [];
    this.showCompanyDropdown = false;
    this.highlightedCompanyIndex = -1;
  },

  highlightNextCompany() {
    if (this.filteredCompanies.length === 0) return;
    this.highlightedCompanyIndex = Math.min(
      this.highlightedCompanyIndex + 1,
      this.filteredCompanies.length - 1
    );
  },

  highlightPreviousCompany() {
    if (this.filteredCompanies.length === 0) return;
    this.highlightedCompanyIndex = Math.max(this.highlightedCompanyIndex - 1, 0);
  },

  selectHighlightedCompany() {
    if (
      this.highlightedCompanyIndex >= 0 &&
      this.highlightedCompanyIndex < this.filteredCompanies.length
    ) {
      const company = this.filteredCompanies[this.highlightedCompanyIndex];
      this.setCompanyFromBrreg(company);
    }
  },

  get availableAddresses() {
    if (this.selectedMethod === 'stripe') return this.addressList;
    return this.addressList.filter(addr => addr.address_type === this.selectedInvoiceType);
  },

  get selectedAddressId() {
    return this.selectedInvoiceType === 'individual'
      ? this._selectedIndividualAddressId
      : this._selectedCompanyAddressId;
  },
  set selectedAddressId(value) {
    if (this.selectedInvoiceType === 'individual') {
      this._selectedIndividualAddressId = value;
    } else {
      this._selectedCompanyAddressId = value;
    }
  },

  // Theme configurations
  themes: {
    stripe: {
      selected: 'text-blue-700 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300 shadow-blue-100',
      checkmark: 'from-blue-500 to-blue-600'
    },
    manual: {
      selected: 'text-orange-700 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300 shadow-orange-100',
      checkmark: 'from-orange-500 to-amber-600'
    },
    vipps: {
      selected: 'text-orange-700 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300 shadow-orange-100',
      checkmark: 'from-orange-500 to-orange-600'
    },
    paypal: {
      selected: 'text-blue-700 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300 shadow-blue-100',
      checkmark: 'from-blue-500 to-blue-600'
    },
    kustom: {
      selected: 'text-indigo-700 bg-gradient-to-br from-indigo-50 to-slate-50 border-indigo-300 shadow-indigo-100',
      checkmark: 'from-indigo-500 to-slate-700'
    },
    invoice: {
      selected: 'text-orange-700 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300 shadow-orange-100',
      checkmark: 'from-orange-500 to-amber-600'
    },
    default: 'text-gray-600 hover:text-gray-800 border-gray-200 hover:border-gray-300 hover:bg-gradient-to-br hover:from-gray-50 hover:to-blue-50'
  },

  unselectedStyles: {
    payment: 'text-gray-600 hover:text-gray-800 border-gray-200 hover:border-gray-300 hover:bg-gradient-to-br hover:from-gray-50 hover:to-blue-50',
    invoice: 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-transparent hover:border-gray-200'
  },

  // Computed properties (getters)
  get showStripeSection() {
    return this.selectedMethod === 'stripe';
  },

  get showInvoiceSection() {
    return this.selectedMethod === 'manual';
  },

  get showCompanyFields() {
    return this.selectedInvoiceType === 'company';
  },

  // Helper methods for styling
  getPaymentOptionClasses(method) {
    if (this.selectedMethod === method) {
      return this.themes[method]?.selected || this.themes.manual.selected;
    }
    return this.unselectedStyles.payment;
  },

  getInvoiceTypeClasses(type) {
    if (this.selectedInvoiceType === type) {
      return this.themes.invoice.selected;
    }
    return this.unselectedStyles.invoice;
  },

  getCheckmarkClasses(value, type) {
    const isSelected = type === 'payment'
      ? this.selectedMethod === value
      : this.selectedInvoiceType === value;

    if (isSelected) {
      const theme = this.themes[value] || this.themes.manual;
      return `bg-gradient-to-br ${theme.checkmark}`;
    }
    return 'bg-gray-200';
  },

  getCheckmarkVisibility(value, type) {
    const isSelected = type === 'payment'
      ? this.selectedMethod === value
      : this.selectedInvoiceType === value;

    return isSelected ? '' : 'opacity-0 group-hover:opacity-40';
  },

  // Event handlers
  selectPaymentMethod(el, gatewayId, method) {
    // Validate that method is provided
    if (!method && gatewayId) {
      const gatewayInput = document.querySelector(`#payment_gateway_${gatewayId}`);
      method = gatewayInput?.dataset?.gatewayKind || 'stripe';
    }

    this.selectedMethod = method || 'stripe';
    this.selectedGatewayId = gatewayId;
    this.$dispatch("remote-debounced-submit", { el: el });
  },

  async createNewAddress() {
    const addressData = { ...this.newAddress, address_type: this.selectedInvoiceType };

    if (!addressData.address1) return;

    try {
      const newAddress = await createAddress(addressData);
      this.addressList.push(newAddress);
      this.selectedAddressId = newAddress.id;
      return newAddress;
    } catch (error) {
      console.error('Error creating new address:', error);
      this.showError('Failed to create new address. Please check your input and try again.');
      throw error;
    }
  },
  async submit() {
    if (this.isLoading) return;

    this.isLoading = true;
    try {
      if (this.selectedAddressId === 'new_address') {
        await this.createNewAddress();
      }
      await this.submitForm();
    } catch (error) {
      console.error('Form submission error:', error);
      this.showError('An error occurred while processing your request. Please try again.');
    } finally {
      this.isLoading = false;
    }
  },

  showError(message) {
    trackCheckoutEvent('client_error_shown', { severity: 'error', message });
    alert(message);
  },

  extractError(data) {
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (data.message) return data.message;
    if (data.errors) {
      if (Array.isArray(data.errors)) return data.errors.join(', ');
      if (typeof data.errors === 'object') return Object.values(data.errors).flat().join(', ');
    }
    return 'An error occurred. Please try again.';
  },

  async submitForm() {
    try {
      const formData = new FormData(this.$el);

      // Create Stripe token for new credit card
      if (this.selectedMethod === 'stripe' && this.selectedCardId === 'new_credit_card' && this.$refs.card) {
        const { token, error } = await this.stripe.createToken(this.cardElement);

        if (error) {
          this.showError(error.message);
          return;
        }

        formData.append('payment[card_token]', token.id);
      }

      const response = await fetch((this.$el).action, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const data = await response.json();

      if (response.ok) {
        if (data.type === 'redirect' && data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (data.redirect_path) {
          window.location.href = data.redirect_path;
        }
      } else if (data.type === "requires_action" || data.type === "requires_confirmation") {
        await this.handleStripePayment(data, data.success_path);
      } else {
        this.showError(this.extractError(data));
      }
    } catch (error) {
      console.error('Form submission error:', error);
      this.showError("Network error occurred");
    } finally {
      this.isLoading = false;
    }
  },

  async handleStripePayment(data, redirect_path) {
    try {
      if (!this.stripe) {
        this.showError("Stripe is not initialized");
        return;
      }

      const { error } = await this.stripe.confirmCardPayment(data.result.stripe_payment_intent.client_secret);

      if (error) {
        this.showError(error.message || "Payment authentication failed");
        return;
      }
      if (redirect_path) {
        window.location.href = redirect_path;
      }
    } finally {
      this.isLoading = false;
    }
  },

  // Cleanup method
  destroy() {
    // Clear any pending timeouts
    if (this.companySearchTimeout) {
      clearTimeout(this.companySearchTimeout);
      this.companySearchTimeout = null;
    }

    // Clean up Stripe elements
    if (this.cardElement) {
      if (this.cardChangeHandler) {
        this.cardElement.off('change', this.cardChangeHandler);
        this.cardChangeHandler = null;
      }
      this.cardElement.unmount();
      this.cardElement = null;
    }
    this.elements = null;
    this.stripe = null;
  }
}));
